/* eslint-disable react/destructuring-assignment */
import React from "react";

import { useSelector } from "react-redux";
import { RootState } from "store/ducks";

import {
  BaseField,
  FlatBox,
} from "pipestyle";

import { GetDaysFromSeconds, GetSecondsBetweenDates } from "utils/TimeOutput";
import DateOutput from "utils/DateOutput";
import { In, Empty, EqualTo } from "utils/Patterns/Specification";
import { cleanStr, historyfieldsFilter, orderHistoryProtocol } from "utils/Pipefy";
import { RemoveAccent } from "utils/String";
import { convertStringToFloatCurrency } from "utils/ConvertStringToFloat";

import { EnumPipefyInputFieldIds, EnumPipefyInputTypes } from "pages/Pipefy/enum";
import { FieldsCardProps, InputsHide, PhasesHistoryFieldsProps } from "pages/Pipefy/interface";
import { IPipefyOption } from "components/Select/interface";
import { IDomainSetting } from "models/DomainSetting";
import { specialDownloadIds } from "./model";

import { Row, Column } from "components/Layout/Grid";
import { DateExpire } from 'components/Pipefy/DateExpire';
import CurrentPhaseHistory from "../CurrentPhaseHistory";
import { Alert } from "components/Alert";

interface IPipefyHistoryProps {
  protocolNumber?: number;

  form: any;
  components: any;
  filesMap: { [key: string]: React.ReactNode[]; };
  allCategories: Array<IPipefyOption>;
  allReasons: Array<IPipefyOption>;
  allClassifications: Array<IPipefyOption>;
  userProfileTypes: Array<IDomainSetting>;

  getInputValueById: Function;
  openCardProtocolLinked: Function;
  showField: Function;
  downloadLinkUnique: Function;
}

const History: React.FC<IPipefyHistoryProps> = (props) => {
  const { form, components, protocolNumber, filesMap } = props;
  const { getInputValueById, allCategories, allClassifications, allReasons, userProfileTypes } = props;
  const { openCardProtocolLinked, showField, downloadLinkUnique } = props;

  const user = useSelector((state: RootState) => state.user);

  function getLotDates(input: any) {
    const inputValue =
      form?.card?.fields?.find((item: FieldsCardProps) => item.field.id === input.id)
        ?.value || "";

    const idInputsHide = new In([
      InputsHide.DATA_FABRICACAO,
      InputsHide.DATA_VALIDADE,
      InputsHide.LOTE,
      InputsHide.DATA_OCORRENCIA_1,
      InputsHide.CARGO,
      InputsHide.HA_SALDO_REMANESCENTE,
      InputsHide.TIPO_OCORRENCIA,
      InputsHide.ESPECIFICACAO_TIPO_OCORRENCIA,
      InputsHide.DESCRICAO_QUEIXA,
      InputsHide.AMOSTRA_CONTAMINADA_DISPONIVEL,
      InputsHide.TIPO_AMOSTRA,
      InputsHide.QTD_RECLAMADA,
      InputsHide.VALOR_DO_DESCONTO
    ]);

    const hideStatusIntegracao = new In([
      InputsHide.STATUS_INTEGRACAO_DEVOLUCAO
    ]);
    const empty = new Empty();
    const zero = new EqualTo('0');

    if (hideStatusIntegracao.isSatisfiedBy(input.id)) {
      return false;
    } else {
      return !(
        idInputsHide.isSatisfiedBy(input.id) && (empty.isSatisfiedBy(inputValue) || zero.isSatisfiedBy(inputValue))
      );
    }
  }

  function setOrderItens(line: any, index: any) {
    const newLine = line.split(":");

    return (
      <p key={index} className="pp-title-description mb-1">
        {newLine[0]}:<span className="span-description">{newLine[1]}</span>
      </p>
    );
  }

  function setAlerts() {
    const alertFields: string[] = [
      InputsHide.STATUS_INTEGRACAO_DEVOLUCAO,
      InputsHide.PROBLEMAS_NO_ENVIO_DE_EMAIL
    ];

    const alerts: FieldsCardProps[] = form.card.fields
      .filter((item: FieldsCardProps) => alertFields.includes(item.field.id) && item.value);

    if (alerts.length === 0) return;

    return (
      alerts.map((item) =>
        (<Alert key={item.field.id} title={item.name} description={item.value} />))
    );
  }

  // Faz De Para nos labels que envia para o pipefy sem acentuação e converte com acentuação na view do CCare
  function getInputValueFromTo(id: string) {
    let inputValuePipefy = cleanStr(
      form?.card?.fields?.find((item: FieldsCardProps) => item.field.id === id)?.value || ""
    );

    // categorias
    let newValueFromTo = allCategories.find(x => x.pipefyLabel === inputValuePipefy)?.label;

    if (newValueFromTo) return newValueFromTo;

    // motivos
    newValueFromTo = allReasons.find(x => x.pipefyLabel === inputValuePipefy)?.label;

    if (newValueFromTo) return newValueFromTo;

    // classificações
    newValueFromTo = allClassifications.find(x => x.pipefyLabel === inputValuePipefy)?.label;

    if (newValueFromTo) return newValueFromTo;

    // grupo responsável
    newValueFromTo = userProfileTypes.find(x =>
      RemoveAccent(x.value).toUpperCase() === RemoveAccent(inputValuePipefy).toUpperCase())?.value?.toUpperCase();

    if (newValueFromTo) return newValueFromTo;

    if (id === 'valor_do_desconto') {
      return convertStringToFloatCurrency(inputValuePipefy);
    }

    if (id === 'valor_total_da_nf' || id === 'valor_da_reclama_o') {
      return `R$ ${inputValuePipefy}`;
    }

    return inputValuePipefy;
  }

  function getNameAttachment(url: string) {
    const partsUrl = url.split("/");
    const unitSelected = partsUrl[partsUrl.length - 1];

    return (
      unitSelected.indexOf("?") !== -1
        ? unitSelected.split("?")[0]
        : decodeURIComponent(unitSelected).split("?")[0]
    );
  }

  function downloadLink(id: string) {
    const item = form?.card?.fields?.find((item: FieldsCardProps) => item.field.id === id);
    if (!item?.value) return;

    const Attachments = JSON.parse(item.value) as string[];
    const files = Attachments.map((url, index) => {
      return (
        <p className="pp-title-description" key={index}>
          <a
            href={url || ""}
            target="_blank"
            rel="noreferrer"
            download
          >
            {getNameAttachment(url)}
          </a>
        </p>);
    });

    return files;
  }

  function historyBaseField(item: PhasesHistoryFieldsProps) {
    if (!item) return;

    const ComponentType = components[item.type];
    const isAttachmentItem = item.type === EnumPipefyInputTypes.ANEXOS;
    const inputValue = getInputValueById(item.id);

    const existInputValue = inputValue &&
      item.type !== "radio_horizontal" &&
      item.type !== "attachment" &&
      inputValue !== "undefined";

    if (specialDownloadIds.includes(item.id as EnumPipefyInputFieldIds)) {
      return downloadLinkUnique(item.id);
    }

    if (existInputValue) {
      return (
        <p className="pp-title-description">
          {inputValue?.split("\n").map((line: string, indexValue: number) => (
            <p key={indexValue} className="pp-title-description mb-1">
              {line}
            </p>
          ))}
        </p>
      );
    }

    if (isAttachmentItem) {
      return downloadLink(item.id);
    }

    if (inputValue !== "undefined") {
      return (
        <ComponentType
          {...item}
          value={inputValue}
          synced_with_card="false"
          disabled
          checked
        />
      );
    }

    return <p className="pp-title-description">-</p>;
  }

  return (
    <Column
      className="p-4 pp-card-left"
      style={{ background: "#E6ECF5", borderRadius: 5 }}
    >
      <h1
        className="pp-title-1 pp-font-bold font-bold"
        style={{ color: "#3b5bfd", fontSize: 22 }}
      >
        {protocolNumber}
      </h1>
      <div>
        <p className="pp-creator-date">
          Criado em{" "}
          <DateOutput
            dateStr={form?.card?.createdAt}
            patternStr="dd/MM/yyyy HH:mm"
          />
        </p>
      </div>

      {setAlerts()}

      <Column className="overflow-y-auto pp-scroll mt-4">
        <DateExpire
          title="Data de vencimento do card"
          shouldExpireAt={form?.card?.expiration?.shouldExpireAt}
          expiredAt={form?.card?.expiration?.expiredAt}
          show
        />
        <Row className="d-block-important">
          <h2
            className="font-bold"
            style={{ fontSize: 14, color: "#293e52" }}
          >
            Formulário Inicial
          </h2>
        </Row>
        <Column
          className="d-block-important bg-white mt-2 p-4"
          style={{ borderRadius: 5 }}
        >
          {(form.card.phases_history[0]?.phase?.fields as any[]).map(
            (item, index) => {
              const ComponentType = components[item.type];

              const isAttachmentItem = item.id === EnumPipefyInputFieldIds.ANEXOS_SUBMETIDOS ||
                item.id === EnumPipefyInputFieldIds.ANEXO_RECLAMACAO_TECNICA;

              if (getLotDates(item)) {
                return (
                  <BaseField
                    key={index}
                    label={
                      item.label.charAt(0).toUpperCase() +
                      item.label.slice(1)
                    }
                    synced_with_card="false"
                  >
                    {getInputValueById(item.id) &&
                      item.type !== "radio_horizontal" ? (
                      isAttachmentItem ? (filesMap[item.id])
                        : item.id === "itens_do_pedido" ||
                          item.id === "descri_o_do_protocolo" ? (
                          <Column
                            className="overflow-y-auto pp-scroll"
                            style={{ maxHeight: 200 }}
                          >
                            {getInputValueById(item.id)
                              ?.split("\n")
                              .map((line: string, indexValue: number) =>
                                item.id === "itens_do_pedido" ? (
                                  setOrderItens(line, indexValue)
                                ) : (
                                  <p
                                    key={indexValue}
                                    className="pp-title-description mb-1"
                                  >
                                    {line}
                                  </p>
                                )
                              )}
                          </Column>
                        ) : item.id === "protocolo_vinculado" ? (
                          <p className="pp-title-description">
                            <a
                              onClick={() =>
                                openCardProtocolLinked(item.id)
                              }
                            >
                              {getInputValueById(item.id)}
                            </a>
                          </p>
                        ) : (
                          <p className="pp-title-description">
                            {getInputValueFromTo(item.id)}
                          </p>
                        )
                    ) : getInputValueById(item.id) ? (
                      <ComponentType
                        {...item}
                        value={getInputValueById(item.id)}
                        disabled
                        checked
                        synced_with_card="false"
                      />
                    ) : (
                      <p className="pp-title-description">-</p>
                    )}
                  </BaseField>
                );
              }
            }
          )}
        </Column>
        <h2
          className="font-bold mt-4"
          style={{ fontSize: 14, color: "#293e52" }}
        >
          Histórico
        </h2>
        <Column
          className="d-block-important mt-2"
          style={{ borderRadius: 3 }}
        >
          {orderHistoryProtocol(form.card.phases_history).map(
            (history: any, _: number) => _ > 0 &&
              (history.phase.name !== form.card.current_phase.name ? (
                <FlatBox
                  key={_}
                  title={
                    <Row className="flex-1 justify-between pr-3">
                      <h3 className="font-bold">
                        {history.phase.name}
                      </h3>
                      <h3 className="font-bold">
                        <DateOutput
                          dateStr={history.lastTimeIn}
                          patternStr="dd/MM"
                        />
                      </h3>
                    </Row>
                  }
                  description={
                    <Row className="flex-1 justify-between pr-3">
                      <span
                        className="pp-title-description font-light mt-1"
                        style={{ fontSize: 12 }}
                      >
                        {GetDaysFromSeconds(GetSecondsBetweenDates(history.lastTimeOut, history.lastTimeIn))} nessa
                        fase
                      </span>
                      <span
                        className="pp-title-description font-light mt-1"
                        style={{ fontSize: 12 }}
                      >
                        <DateOutput
                          dateStr={history.lastTimeIn}
                          patternStr="H:mm a"
                        />
                      </span>
                    </Row>
                  }
                >
                  {historyfieldsFilter((history.phase.fields as any[]), history.phase.name, user).map(
                    (item, index) => {
                      return (
                        showField(item) && (
                          <BaseField
                            key={index}
                            label={
                              item.label.charAt(0).toUpperCase() +
                              item.label.slice(1)
                            }
                            synced_with_card="false"
                          >
                            {historyBaseField(item)}
                          </BaseField>
                        )
                      );
                    }
                  )}
                </FlatBox>
              ) : (
                <CurrentPhaseHistory key={_} history={history} />
              ))
          )}
        </Column>
      </Column>
    </Column>
  );
};

export default History;